import { motion, useAnimate, useMotionValue, useTransform } from 'framer-motion';
import { Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

interface SwipeToDeleteProps {
	children: ReactNode;
	onDelete: () => void;
}

const SWIPE_THRESHOLD = -180;

const SwipeToDelete = ({ children, onDelete }: SwipeToDeleteProps) => {
	const [isDeleting, setIsDeleting] = useState(false);
	const [scope, animate] = useAnimate();
	const isDraggingRef = useRef(false);

	// Motion values for smooth animations
	const x = useMotionValue(0);
	const opacity = useTransform(x, [SWIPE_THRESHOLD, 0], [1, 0]);
	const scale = useTransform(x, [SWIPE_THRESHOLD, 0], [1, 0.6]);

	const handleDragStart = () => {
		isDraggingRef.current = true;
	};

	const handleDragEnd = async (
		_event: MouseEvent | TouchEvent | PointerEvent,
		info: { offset: { x: number } }
	) => {
		isDraggingRef.current = false;

		if (info.offset.x < SWIPE_THRESHOLD) {
			setIsDeleting(true);
		} else {
			// Explicitly reset position if threshold not met
			await animate(x, 0, {
				type: 'spring',
				stiffness: 500,
				damping: 30
			});
		}
	};

	// Reset handler for stuck cards (tap to reset)
	const handleTap = () => {
		if (!isDraggingRef.current && !isDeleting && x.get() !== 0) {
			animate(x, 0, {
				type: 'spring',
				stiffness: 500,
				damping: 30
			});
		}
	};

	const handleAnimationComplete = () => {
		if (isDeleting) {
			onDelete();
		}
	};

	return (
		<motion.div
			ref={scope}
			className="relative overflow-hidden rounded-md"
			animate={
				isDeleting
					? { opacity: 0, maxHeight: 0, marginBottom: 0 }
					: { opacity: 1, maxHeight: 1000, marginBottom: undefined }
			}
			transition={{ duration: 0.3, ease: 'easeOut' }}
			onAnimationComplete={handleAnimationComplete}
		>
			{/* Delete background */}
			<motion.div
				style={{ opacity }}
				className="absolute inset-0 bg-destructive flex items-center justify-end pr-6 rounded-md pointer-events-none"
			>
				<motion.div style={{ scale }}>
					<Trash2 className="h-6 w-6 text-destructive-foreground" />
				</motion.div>
			</motion.div>

			{/* Draggable content */}
			<motion.div
				drag="x"
				dragConstraints={{ left: -200, right: 0 }}
				dragElastic={0.2}
				dragSnapToOrigin
				dragPropagation={false}
				dragMomentum={false}
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
				onTap={handleTap}
				style={{ x, touchAction: 'pan-y' }}
				className="select-none"
				role="button"
				aria-label="Swipe left to delete"
				tabIndex={isDeleting ? -1 : 0}
			>
				{children}
			</motion.div>
		</motion.div>
	);
};

export default SwipeToDelete;
